import {
    ReactElement,
    useMemo,
    useState,
    useCallback,
    useEffect,
    FormEventHandler,
    useRef
} from 'react';
import { CreateCompanyUserInvitesRequest } from '@/api';
import { useCompanyContext } from '../providers';
import { useInviteUsersAction } from '../actions';
import { useFetchUserCounts, useFetchTeamOptions } from '../queries';
import {
    UIModal,
    UIAnimation,
    UIFormInputRef,
    UIFormTextarea,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UIMultiSelectInput,
    UIPaginatedMultiSelectInput,
    UIPaginatedSelectOption,
    UISelectInput,
    UISelectValue,
    UISwitch,
    UIText
} from '@/ui';
import styles from './invite-users-modal.module.scss';
import { validateEmail } from '@/common/util';
import emailInviteSent from '@/common/assets/lotties/email-invite-sent.json';
import { useCompaniesTranslation } from '../hooks';

type InviteUsersModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

export function InviteUsersModal(props: InviteUsersModalProps): ReactElement {
    const { isOpen, onClose } = props;

    const { inviteUsers, loading } = useInviteUsersAction();
    const { company } = useCompanyContext();
    const { counts } = useFetchUserCounts(company.id);
    const [showSuccess, setShowSuccess] = useState(false);

    const subscriptionStatus = useMemo(() => {
        return company.subscription?.status ?? 'none';
    }, [company.subscription]);

    const licenseCount = useMemo(() => {
        if (company.subscription) {
            return company.subscription.quantity;
        }
        return 0;
    }, [company.subscription]);

    // Until the counts land, assume every seat is taken so the licence toggle
    // fails closed. In practice unreachable: the modal only mounts from the
    // members page, which has already fetched the same query.
    const usedLicenseCount = counts?.assignedLicense ?? licenseCount;

    function handleClose() {
        onClose();
        setShowSuccess(false);
    }

    function onSubmit(data: CreateCompanyUserInvitesRequest) {
        inviteUsers(data)
            .then(() => {
                setShowSuccess(true);
            })
            .catch(() => {}); // Error handled in hook
    }

    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            <InviteUsersModalForm
                companyId={company.id}
                isOpen={isOpen}
                subscriptionStatus={subscriptionStatus}
                licenseCount={licenseCount}
                usedLicenseCount={usedLicenseCount}
                onClose={handleClose}
                loading={loading}
                onSubmit={onSubmit}
                showSuccess={showSuccess}
                setShowSuccess={setShowSuccess}
            />
        </UIModal>
    );
}

type InviteUsersModalFormProps = {
    companyId: string;
    subscriptionStatus:
        | 'active'
        | 'canceled'
        | 'incomplete'
        | 'incomplete_expired'
        | 'past_due'
        | 'paused'
        | 'trialing'
        | 'unpaid'
        | 'none';
    licenseCount: number;
    usedLicenseCount: number;
    isOpen: boolean;
    loading: boolean;
    onClose: () => void;
    onSubmit: (data: CreateCompanyUserInvitesRequest) => void;
    showSuccess: boolean;
    setShowSuccess: (value: boolean) => void;
};

function InviteUsersModalForm(props: InviteUsersModalFormProps): ReactElement {
    const { isOpen, showSuccess } = props;

    return (
        <>
            {isOpen ? (
                showSuccess ? (
                    <SuccessContent {...props} />
                ) : (
                    <FormContent {...props} />
                )
            ) : null}
        </>
    );
}

function FormContent(props: InviteUsersModalFormProps): ReactElement {
    const { companyId, loading, onClose, onSubmit } = props;
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    const { t } = useCompaniesTranslation();

    /**
     * Emails
     */
    const emailsRef = useRef<UIFormInputRef>(null);
    const emailDefault = '';
    const [emailText, setEmailText] = useState(emailDefault);
    const emails = useMemo(() => {
        const emailList = emailText
            .split(',')
            .map((email) => email.trim().toLowerCase());

        // Remove empty string if the last character is a comma
        if (emailList[emailList.length - 1] === '') {
            emailList.pop();
        }

        return emailList;
    }, [emailText]);
    const [emailsError, setEmailsError] = useState('');
    // Addresses already invited or joined are rejected by the server on submit
    // — the client no longer holds the roster needed to catch them up front.
    const checkEmails = useCallback(
        (emails: string[]): string => {
            const uniqueEmails = new Set(emails);

            if (emails.length === 0) {
                return t('components.invite_users_modal.emails_error_empty');
            }

            if (emails.length > 100) {
                return t('components.invite_users_modal.emails_error_max');
            }

            for (const email of emails) {
                if (validateEmail(email) !== '') {
                    return t(
                        'components.invite_users_modal.error_invalid_email',
                        { email }
                    );
                }
            }

            if (uniqueEmails.size !== emails.length) {
                const duplicates = emails.filter(
                    (email, idx) => emails.indexOf(email) !== idx
                );
                const uniqueDuplicates = [...new Set(duplicates)];
                if (uniqueDuplicates.length === 1) {
                    return t(
                        'components.invite_users_modal.error_duplicate_email',
                        { email: uniqueDuplicates[0] }
                    );
                }
                return t(
                    'components.invite_users_modal.error_duplicate_emails',
                    { emails: uniqueDuplicates.join(', ') }
                );
            }

            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setEmailsError(checkEmails(emails));
        }
    }, [emails, showRealTimeErrors, checkEmails]);

    /**
     * Company Roles
     */
    const companyRolesOptions: UISelectValue[] = useMemo(
        () => [
            {
                label: t('shared.roles.company.admin.name'),
                subLabel: t('shared.roles.company.admin.description'),
                value: 'admin'
            },
            {
                label: t('shared.roles.company.billing.name'),
                subLabel: t('shared.roles.company.billing.description'),
                value: 'billing'
            },
            {
                label: t('shared.roles.company.member.name'),
                subLabel: t('shared.roles.company.member.description'),
                value: 'member'
            }
        ],
        [t]
    );
    const companyRolesDefault = companyRolesOptions[2];
    const [companyRoles, setCompanyRoles] = useState<UISelectValue | undefined>(
        companyRolesDefault
    );
    const [companyRolesError, setCompanyRolesError] = useState('');

    const checkCompanyRoles = useCallback(
        (selectedCompanyRoles: UISelectValue | undefined): string => {
            if (!selectedCompanyRoles) {
                return t(
                    'components.invite_users_modal.company_role_error_required'
                );
            }

            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setCompanyRolesError(checkCompanyRoles(companyRoles));
        }
    }, [companyRoles, showRealTimeErrors, checkCompanyRoles]);

    /**
     * License
     */
    const licenseDefault = false;
    const [license, setLicense] = useState<boolean>(licenseDefault);
    const [licenseError, setLicenseError] = useState('');
    const remainingLicenses = props.licenseCount - props.usedLicenseCount;
    const remainingLicensesAfterInvite =
        props.licenseCount - (props.usedLicenseCount + emails.length);
    const [licenseSubText, licenseSubTextType] = useMemo<
        [string, 'error' | 'default2']
    >(() => {
        if (['active', 'past_due'].includes(props.subscriptionStatus)) {
            if (remainingLicenses > 0) {
                return [
                    t('components.invite_users_modal.licenses_available', {
                        count: remainingLicenses
                    }),
                    'default2'
                ];
            }
            return [t('components.invite_users_modal.no_licenses'), 'error'];
        }

        if (props.subscriptionStatus === 'trialing') {
            return [
                t('components.invite_users_modal.license_trial_unlimited'),
                'default2'
            ];
        }

        return [
            t('components.invite_users_modal.license_no_subscription'),
            'error'
        ];
    }, [props.subscriptionStatus, remainingLicenses, t]);
    const isLicenseDisabled = useMemo(() => {
        if (['active', 'past_due'].includes(props.subscriptionStatus)) {
            if (remainingLicenses > 0) {
                return false;
            } else {
                return true;
            }
        }

        if (props.subscriptionStatus === 'trialing') {
            return false;
        }

        return true;
    }, [props.subscriptionStatus, remainingLicenses]);
    const checkLicense = useCallback(
        (license: boolean): string => {
            if (!license) {
                return '';
            }
            if (props.subscriptionStatus === 'trialing') {
                return '';
            }
            if (!['active', 'past_due'].includes(props.subscriptionStatus)) {
                return t(
                    'components.invite_users_modal.license_error_no_subscription'
                );
            }
            if (remainingLicensesAfterInvite < 0) {
                return t(
                    'components.invite_users_modal.license_error_not_enough'
                );
            }
            return '';
        },
        [props.subscriptionStatus, remainingLicensesAfterInvite, t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setLicenseError(checkLicense(license));
        }
    }, [license, showRealTimeErrors, checkLicense]);
    useEffect(() => {
        if (
            !['active', 'past_due'].includes(props.subscriptionStatus) ||
            remainingLicenses <= 0
        ) {
            setLicense(false);
        }
    }, [props.subscriptionStatus, remainingLicenses]);

    /**
     * Teams
     */
    const teamsDefault: UIPaginatedSelectOption[] = useMemo(() => [], []);
    const [teams, setTeams] = useState<UIPaginatedSelectOption[]>(teamsDefault);
    const [teamsError, setTeamsError] = useState('');
    const teamPicker = useFetchTeamOptions({
        companyId,
        roles: ['admin', 'guardian']
    });
    // Teams are optional on an invite, so there is nothing to validate — kept
    // as a hook so it mirrors the other fields on this form.
    const checkTeams = useCallback((): string => {
        return '';
    }, []);
    useEffect(() => {
        if (showRealTimeErrors) {
            setTeamsError(checkTeams());
        }
    }, [teams, showRealTimeErrors, checkTeams]);

    /**
     * Team Roles
     */
    const teamRolesDefault: UISelectValue[] = [];
    const [teamRoles, setTeamRoles] =
        useState<UISelectValue[]>(teamRolesDefault);
    const [teamRolesError, setTeamRolesError] = useState('');
    const teamRolesOptions: UISelectValue[] = useMemo(() => {
        const admin: UISelectValue = {
            label: t('shared.roles.team.admin.name'),
            subLabel: t('shared.roles.team.admin.description'),
            value: 'admin'
        };
        const guardian: UISelectValue = {
            label: t('shared.roles.team.guardian.name'),
            subLabel: t('shared.roles.team.guardian.description'),
            value: 'guardian'
        };
        const memberOnly: UISelectValue = {
            label: t('shared.roles.team.member.name'),
            subLabel: t('shared.roles.team.member.description'),
            value: 'member'
        };

        // If roles contains member, only show member
        if (teamRoles.some((role) => role.value === 'member')) {
            return [memberOnly];
        } else if (teamRoles.length > 0) {
            return [admin, guardian];
        } else {
            return [admin, guardian, memberOnly];
        }
    }, [teamRoles, t]);
    useEffect(() => {
        const memberRole = teamRoles.find((role) => role.value === 'member');
        if (memberRole && teamRoles.length > 1) {
            setTeamRoles([memberRole]);
        }
    }, [teamRoles]);

    const checkTeamRoles = useCallback(
        (selectedTeamRoles: UISelectValue[]): string => {
            if (teams.length === 0) {
                return '';
            }

            if (selectedTeamRoles.length === 0) {
                return t(
                    'components.invite_users_modal.team_roles_error_required'
                );
            }

            return '';
        },
        [teams, t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setTeamRolesError(checkTeamRoles(teamRoles));
        }
    }, [teamRoles, showRealTimeErrors, checkTeamRoles]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (
            !checkEmails(emails) &&
            !checkCompanyRoles(companyRoles) &&
            !checkLicense(license) &&
            !checkTeams() &&
            !checkTeamRoles(teamRoles)
        ) {
            setShowRealTimeErrors(false);

            let companyRoleValue: 'owner' | 'admin' | 'billing' | 'member' =
                'member';
            if (companyRoles?.value === 'owner') {
                companyRoleValue = 'owner';
            } else if (companyRoles?.value === 'admin') {
                companyRoleValue = 'admin';
            } else if (companyRoles?.value === 'billing') {
                companyRoleValue = 'billing';
            }

            const _teamRoles: ('admin' | 'guardian')[] = [];
            if (teams.length > 0) {
                const hasAdmin = teamRoles.some(
                    (role) => role.value === 'admin'
                );
                const hasGuardian = teamRoles.some(
                    (role) => role.value === 'guardian'
                );
                if (hasAdmin) {
                    _teamRoles.push('admin');
                }
                if (hasGuardian) {
                    _teamRoles.push('guardian');
                }
            }

            onSubmit?.({
                companyId,
                emails,
                teamIds: teams.map((team) => team.value),
                companyRole: companyRoleValue,
                teamRoles: _teamRoles,
                license: license
            });
        }
    };

    // const resetDefaults = useCallback(() => {
    //     setEmailText(emailDefault);
    //     setCompanyRoles(companyRolesDefault);
    //     setLicense(licenseDefault);
    //     setTeams(teamsDefault);
    //     setTeamRoles(teamRoles);
    //     setShowRealTimeErrors(false);
    // }, [
    //     emailDefault,
    //     companyRolesDefault,
    //     licenseDefault,
    //     teamRoles,
    //     teamsDefault
    // ]);

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UIModalHeader title={t('components.invite_users_modal.title')} />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <UIFormTextarea
                        ref={emailsRef}
                        name="Emails"
                        label={t('components.invite_users_modal.emails_label')}
                        value={emailText}
                        description={t(
                            'components.invite_users_modal.emails_description'
                        )}
                        placeholder={t(
                            'components.invite_users_modal.emails_placeholder'
                        )}
                        autoComplete="on"
                        errorText={showRealTimeErrors ? emailsError : ''}
                        required
                        maxLength={10000}
                        maxHeight={150}
                        onChange={setEmailText}
                        autoFocus
                    />
                    <div className={styles.dualColumnInputContainer}>
                        <UISelectInput
                            label={t('shared.fields.company_role.label')}
                            placeholder={t(
                                'shared.fields.company_role.placeholder'
                            )}
                            description={t(
                                'components.invite_users_modal.company_role_description'
                            )}
                            required={true}
                            options={companyRolesOptions}
                            value={companyRoles}
                            onChange={setCompanyRoles}
                            errorText={
                                showRealTimeErrors ? companyRolesError : ''
                            }
                            style={{ flex: 1 }}
                        />
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                justifyContent: 'space-between',
                                gap: 8
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.invite_users_modal.assign_license'
                                    )}
                                </UIText>

                                <UIText variant="small" type="default2">
                                    {t(
                                        'components.invite_users_modal.license_explainer'
                                    )}
                                </UIText>

                                <div style={{ paddingTop: 4 }}>
                                    {licenseError ? (
                                        <UIText variant="small" type="error">
                                            {licenseError}
                                        </UIText>
                                    ) : (
                                        <UIText
                                            variant="small"
                                            type={licenseSubTextType}
                                        >
                                            {licenseSubText}
                                        </UIText>
                                    )}
                                </div>
                            </div>

                            <UISwitch
                                checked={license}
                                onChange={setLicense}
                                disabled={isLicenseDisabled}
                            />
                        </div>
                    </div>
                    <div className={styles.dualColumnInputContainer}>
                        <UIPaginatedMultiSelectInput
                            label={t(
                                'components.invite_users_modal.teams_label'
                            )}
                            placeholder={t('shared.labels.select')}
                            description={t(
                                'components.invite_users_modal.teams_description'
                            )}
                            searchPlaceholder={t(
                                'components.invite_users_modal.teams_search_placeholder'
                            )}
                            emptyText={t(
                                'components.invite_users_modal.teams_empty'
                            )}
                            errorStateText={t('shared.errors.load_teams')}
                            required={false}
                            options={teamPicker.options}
                            value={teams}
                            onChange={setTeams}
                            search={teamPicker.search}
                            onSearchChange={teamPicker.setSearch}
                            isLoading={teamPicker.status === 'pending'}
                            isError={teamPicker.status === 'error'}
                            isFetchingNextPage={teamPicker.isFetchingNextPage}
                            hasNextPage={teamPicker.hasNextPage}
                            onLoadMore={teamPicker.fetchNextPage}
                            onRetry={teamPicker.refetch}
                            errorText={teamsError}
                            style={{ flex: 1 }}
                        />
                        {teams.length > 0 && (
                            <UIMultiSelectInput
                                label={t('shared.fields.team_roles.label')}
                                placeholder={t('shared.labels.select')}
                                description={t(
                                    'components.invite_users_modal.team_roles_description'
                                )}
                                required
                                options={teamRolesOptions}
                                value={teamRoles}
                                onChange={setTeamRoles}
                                errorText={teamRolesError}
                                style={{ flex: 1 }}
                            />
                        )}
                    </div>
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: t('components.invite_users_modal.submit')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        disabled: loading,
                        onClick: onClose
                    }
                ]}
            />
        </form>
    );
}

function SuccessContent(props: InviteUsersModalFormProps): ReactElement {
    const { onClose, setShowSuccess } = props;

    const { t } = useCompaniesTranslation();
    return (
        <>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 32,
                    gap: 16
                }}
            >
                <UIAnimation
                    animationData={emailInviteSent}
                    loop={false}
                    style={{
                        height: 400
                    }}
                />
                <UIText variant="h1" textAlign="center">
                    {t('components.invite_users_modal.success_title')}
                </UIText>
                <UIText textAlign="center">
                    {t('components.invite_users_modal.success_message')}
                </UIText>
            </div>
            <UIModalFooter
                actions={[
                    {
                        text: t('components.invite_users_modal.done'),
                        variant: 'primary',
                        onClick: onClose
                    },
                    {
                        text: t('components.invite_users_modal.invite_more'),
                        onClick: () => {
                            setShowSuccess(false);
                        }
                    }
                ]}
            />
        </>
    );
}
